import React, { useState } from "react"
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react"
import ColorCircle from "components/ReactHookForm/ColorSelect/ColorCircle"
import { HexColorPicker } from "react-colorful"
import {
  Control, FieldValues, FieldPath, FieldPathValue, useController,
} from "react-hook-form"
import { ColorValue } from "sharedTypes/badge"
import { MIXED, useIsMixed } from "components/CanvasEditor/Inspector/MixedValues"
import useHexDraft from "components/CanvasEditor/Inspector/useHexDraft"
import { colorIsValid, colorWithHash } from "utilities/colors"
import { usePrinterColorConstraints } from "../hooks/usePrinterColorConstraints"
import normalizeToColorValue from "../utils/colorUtils"

type Props<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  control: Control<T>
  label?: string
  showHelpText?: boolean
  defaultValue?: FieldPathValue<T, FieldPath<T>>
  placeholder?: string
  allowClear?: boolean
  onClear?: () => void
  size?: "sm" | "md" | "lg"
}

const ConstrainedColorPicker = <T extends FieldValues = FieldValues>({
  name,
  control,
  label,
  showHelpText = true,
  defaultValue = "#000000" as FieldPathValue<T, FieldPath<T>>,
  placeholder = "Select color...",
  allowClear = false,
  onClear,
  size = "md",
}: Props<T>) => {
  const { field } = useController<T, FieldPath<T>>({
    name,
    control,
    defaultValue,
  })

  const {
    allowedColors, helpText, isColorAllowed, colorMode,
  } = usePrinterColorConstraints()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const defaultColorValue = (defaultValue as ColorValue | undefined) || "#000000"
  const color = normalizeToColorValue(field.value, defaultColorValue)
  const hexDraft = useHexDraft(color)
  // The color last picked here, warned about while it's still the field's
  const [picked, setPicked] = useState<ColorValue | null>(null)
  const showValidationWarning = picked === color && !isColorAllowed(color)
  const mixed = useIsMixed(name)

  const pick = (newColor: ColorValue) => {
    field.onChange(newColor)
    setPicked(newColor)
  }

  const handleColorChange = (newColor: string) => {
    if (colorIsValid(newColor)) pick(colorWithHash(newColor) as ColorValue)
  }

  const handleTextInputChange = (input: string) => {
    const typed = hexDraft.type(input)
    if (typed) pick(typed as ColorValue)
  }

  const getDisplayText = () => {
    if (mixed) return MIXED
    if (!color) return placeholder

    return color
  }

  return (
    <VStack spacing={2} align="stretch">
      <Box>
        {label && (
          <Text fontSize="sm" fontWeight="semibold" mb={2}>
            {label}
          </Text>
        )}

        <Popover
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          placement="right-start"
          closeOnBlur
          closeOnEsc
        >
          <PopoverTrigger>
            <Button
              variant="outline"
              size={size}
              w="full"
              justifyContent="flex-start"
              bg="white"
              _hover={{ bg: "gray.50" }}
              _focus={{ bg: "gray.50", boxShadow: "none" }}
              borderColor={showValidationWarning ? "orange.300" : "gray.300"}
              px={size === "sm" ? 2 : 3}
            >
              <HStack spacing={size === "sm" ? 2 : 3}>
                {!mixed && (
                <Box
                  w={size === "sm" ? 3 : 4}
                  h={size === "sm" ? 3 : 4}
                  borderRadius="sm"
                  bg={color}
                  borderWidth={1}
                  borderColor="gray.400"
                  flexShrink={0}
                />
                )}
                <Text fontSize="sm" color="black">
                  {getDisplayText()}
                </Text>
              </HStack>
            </Button>
          </PopoverTrigger>

          <Portal>
            <PopoverContent w="320px" zIndex={99999}>
              <PopoverArrow />
              <PopoverBody>
                <VStack spacing={4} p={2}>
                  {/* Help Text */}
                  {showHelpText && (
                    <Box w="full">
                      <Text fontSize="xs" color="gray.600" mb={2}>
                        {helpText}
                      </Text>
                    </Box>
                  )}

                  {/* Color Palette */}
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="bold" mb={3}>
                      Available Colors
                    </Text>
                    <HStack wrap="wrap" spacing={2}>
                      {allowedColors.slice(0, 12).map((swatch) => (
                        <ColorCircle
                          key={swatch}
                          color={swatch}
                          selected={!mixed && color === swatch}
                          onSelect={handleColorChange}
                        />
                      ))}
                    </HStack>
                  </Box>

                  {/* Custom Color Picker - Only show for full-color printers */}
                  {colorMode === "full-color" && (
                    <Box w="full">
                      <Text fontSize="sm" fontWeight="bold" mb={3}>
                        Custom Color
                      </Text>
                      <VStack spacing={3}>
                        <HexColorPicker
                          color={color}
                          onChange={handleColorChange}
                        />
                        <HStack w="full">
                          <Input
                            value={hexDraft.text}
                            onChange={(e) => handleTextInputChange(e.target.value)}
                            placeholder="#000000"
                            size="sm"
                            bg="white"
                          />
                        </HStack>
                      </VStack>
                    </Box>
                  )}

                  {/* Clear Button */}
                  {allowClear && color && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="gray"
                      onClick={() => {
                        if (onClear) {
                          onClear()
                        } else {
                          field.onChange("#000000")
                        }
                        onClose()
                      }}
                      w="full"
                    >
                      Clear
                    </Button>
                  )}

                  {/* Validation Warning */}
                  {showValidationWarning && (
                    <Alert status="warning" size="sm">
                      <AlertIcon />
                      <AlertDescription fontSize="xs">
                        This color may not print correctly with your selected
                        printer and label combination.
                      </AlertDescription>
                    </Alert>
                  )}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    </VStack>
  )
}

export default ConstrainedColorPicker
